/**
 * The rest of what the database knows about a venue: its organiser and
 * whether a newsletter is signed up for it, and what is coming up there.
 *
 * Split out of `venueSourcing.ts` rather than folded into it: that file
 * answers one question (who harvests this place), this answers "what do we
 * hold on it" — a different query shape (an organisation lookup, an event
 * list) that happens to be read by the same admin screens. No imports
 * beyond the Supabase client, so it is safe to import from `admin/` as well
 * as `app/` for the same reason `sourcing.ts` and `venueSourcing.ts` are.
 */
import { supabase } from './supabase'
import { fetchAll } from './fetchAll'
import type { FilmFacts } from './types'

export interface OrganisationInfo {
  name: string
  website: string | null
  /** `organisation_mail_sources.mail_source` — the address(es) a newsletter
   *  from this organiser arrives at. Empty means no newsletter is signed up
   *  for it right now, whatever `organisations.website` says. */
  mailSources: string[]
}

/** Null when the venue has no `organisation_id` at all — most galleries and
 *  every cinema, which are filed under a venue and no organiser. */
export async function fetchOrganisation(organisationId: string | null): Promise<OrganisationInfo | null> {
  if (!organisationId) return null

  const [org, sources] = await Promise.all([
    supabase.from('organisations').select('name,website').eq('id', organisationId).maybeSingle(),
    supabase.from('organisation_mail_sources').select('mail_source').eq('organisation_id', organisationId),
  ])
  if (org.error) throw org.error
  if (sources.error) throw sources.error
  if (!org.data) return null

  return {
    name: org.data.name,
    website: org.data.website,
    mailSources: (sources.data ?? []).map(s => s.mail_source),
  }
}

/**
 * Every column either the admin console's global Events tab or a single
 * venue's own "Coming up" table reads — the same shape for both since 23
 * September, when the venue panel grew a table to match the global one
 * (`admin/src/EventsTable.tsx`, `admin/src/eventFields.ts`). Widened from a
 * six-column shape that only ever printed a title and a date range: the
 * admin console's own stated rule for a venue's Record section — show what
 * the database actually holds — applies the same way to what a venue has
 * coming up, and `performers`/`film` are exactly the columns that vary by
 * `type` and used to go unread here.
 *
 * The phone app's `VenueAdmin.tsx` reads this too and still only uses
 * `title`/`run_start`/`run_end` — the extra columns cost it nothing, since
 * nothing there constructs a `VenueEvent`, only reads one `feed` already
 * gives every field of.
 */
export interface VenueEvent {
  id: string
  title: string
  type: string
  discipline: string | null
  run_start: string | null
  run_end: string | null
  published_on: string | null
  venue: string | null
  venue_id: string | null
  building: string | null
  district: string | null
  is_free: boolean | null
  price_min: number | null
  price_max: number | null
  price_note: string | null
  english_access: string | null
  event_lang: string | null
  performers: Record<string, string> | null
  film: FilmFacts | null
  next_showing: string | null
  next_showing_venue: string | null
  showing_count: number | null
  opening_on: string | null
  url: string | null
  booking_url: string | null
  status: string
}

// One literal, not built with `+` — see the identical note by `VENUE_FIELDS`
// in `admin/src/App.tsx`: postgrest-js infers the returned row shape from
// this string at the type level, and concatenation widens it to plain
// `string`, which broke `fetchUpcomingEvents`'s own `as VenueEvent` casts
// the moment this grew past one line.
export const EVENT_FIELDS = 'id,title,type,discipline,run_start,run_end,published_on,venue,venue_id,building,district,is_free,price_min,price_max,price_note,english_access,event_lang,performers,film,next_showing,next_showing_venue,showing_count,opening_on,url,booking_url,status'

/**
 * What is coming up at this venue, soonest first — `feed` rows filed here
 * (`venue_id` or, via a hall, `building_id`), plus anything only *showing*
 * here through `showing_list`, the same union `venue_source_recipes` reads
 * and for the same reason: a film is filed at whichever cinema the parser
 * saw first and plays at several more, and a cinema's admin page asking
 * only `venue_id = X` misses most of its actual programme.
 *
 * Reads `feed` rather than `entries` directly: the view already drops
 * cancelled, past and audience-restricted rows, so nothing here has to
 * restate that filter.
 *
 * The view does *not* drop twins (`twins.ts`), so the three `feed` reads in this
 * file say `duplicate_of is null` themselves. These are fresh reads, never the
 * IndexedDB cache, so the column is always there — once the migration is live.
 */
export async function fetchUpcomingEvents(venueId: string, limit = 15): Promise<VenueEvent[]> {
  const [direct, showings] = await Promise.all([
    supabase.from('feed').select(EVENT_FIELDS)
      .is('duplicate_of', null)
      .or(`venue_id.eq.${venueId},building_id.eq.${venueId}`),
    supabase.from('showing_list').select('entry_id')
      .eq('building_id', venueId)
      .gte('starts_at', new Date().toISOString()),
  ])
  if (direct.error) throw direct.error
  if (showings.error) throw showings.error

  const rows = new Map<string, VenueEvent>()
  for (const r of direct.data ?? []) rows.set(r.id, r as VenueEvent)

  const missing = [...new Set((showings.data ?? []).map(s => s.entry_id))]
    .filter((id): id is string => !!id && !rows.has(id))
  if (missing.length) {
    const extra = await supabase.from('feed').select(EVENT_FIELDS).is('duplicate_of', null).in('id', missing)
    if (extra.error) throw extra.error
    for (const r of extra.data ?? []) rows.set(r.id, r as VenueEvent)
  }

  const sortKey = (e: VenueEvent) => e.run_start ?? e.run_end ?? '9999-12-31'
  return [...rows.values()].sort((a, b) => (sortKey(a) < sortKey(b) ? -1 : 1)).slice(0, limit)
}

/**
 * How many upcoming events every venue has, for the whole list at once — the
 * "has an event / has none" filter, the "most events first" sort and the
 * completion stat all need this for every row, and `fetchUpcomingEvents`
 * above is deliberately one venue at a time, which would be hundreds of
 * round trips to answer any of them.
 *
 * A count, not a flag, and distinct by entry — the same reason
 * `fetchUpcomingEvents` keys its own result by entry id: a concert filed
 * directly at a hall shows up once in the direct half and, because
 * `showing_list` is built from every performance including that one, once
 * more in the showings half. Adding the two counts would double it.
 *
 * `feed` already carries the venue and the building both (a hall rolls up
 * to its building automatically), so the direct half needs one query.
 * `showing_list` is unbounded — 2716 rows once already silently truncated
 * to 1000 (see `fetchAll.ts`) — so it goes through `fetchAll`, ordered by
 * `id` as that file requires for its own pagination to be safe.
 */
export async function fetchUpcomingEventCounts(): Promise<Map<string, number>> {
  const [direct, showings] = await Promise.all([
    fetchAll<{ id: string; venue_id: string | null; building_id: string | null }>(() =>
      supabase.from('feed').select('id,venue_id,building_id').is('duplicate_of', null).order('id')),
    fetchAll<{ entry_id: string | null; building_id: string | null }>(() =>
      supabase.from('showing_list').select('entry_id,building_id')
        .gte('starts_at', new Date().toISOString())
        .order('id')),
  ]);
  if (direct.error) throw new Error(direct.error.message);
  if (showings.error) throw new Error(showings.error.message);

  const entriesByVenue = new Map<string, Set<string>>();
  const add = (venueId: string | null, entryId: string | null) => {
    if (!venueId || !entryId) return;
    let s = entriesByVenue.get(venueId);
    if (!s) { s = new Set(); entriesByVenue.set(venueId, s); }
    s.add(entryId);
  };
  for (const r of direct.data) {
    add(r.venue_id, r.id);
    add(r.building_id, r.id);
  }
  for (const r of showings.data) {
    add(r.building_id, r.entry_id);
  }

  const counts = new Map<string, number>();
  for (const [venueId, entries] of entriesByVenue) counts.set(venueId, entries.size);
  return counts;
}
